/* @flow */
import ActivityRecipient from 'entities/ActivityRecipient';
import ActivityRecipients from 'collections/ActivityRecipients';
import type { Action } from 'action';

type ActivityRecipientsMap = {
  currentUserRecipient?: ActivityRecipient,
  loadingCurrentUserRecipient?: boolean,
  recipients?: ActivityRecipients,
  loadingRecipients?: boolean,
}

type ActivitiesToActivityRecipientMap = {
  [key: string]: ActivityRecipientsMap,
};

export type State = {
  activities: ActivitiesToActivityRecipientMap,
};

export const initialState: State = {
  activities: {},
};

function updateActivity(state: State, activityId: string, values: Object) {
  return {
    activities: {
      ...state.activities,
      [activityId]: {
        ...state.activities[activityId],
        ...values,
      },
    },
  };
}

function updateActivityRecipient(state: State, activityId: string, activityRecipient: ActivityRecipient) {
  const activity = state.activities[activityId] || {};
  const recipients = activity.recipients || new ActivityRecipients();

  recipients.add(activityRecipient);
  return updateActivity(state, activityId, { recipients });
}

// typecheck: ignore statement
export default function reducer(state: State = initialState, action: Action): State {
  switch (action.type) {
    case 'activityRecipients/CURRENT_USER_ACTIVITY_SUCCESS':
    case 'activityRecipients/UPDATE_LOCAL_CURRENT_USER_ACTIVITY':
      return updateActivity(state, action.activityId, {
        currentUserRecipient: action.activityRecipient,
        loadingCurrentUserRecipient: false,
      });
    case 'activities/LOAD_ACTIVITIES_SUCCESS':
      return action.activities.models.reduce((newState, activity) => {
        return updateActivity(newState, activity.id, {
          currentUserRecipient: activity.get('viewing_user_response'),
          loadingCurrentUserRecipient: false,
        });
      }, state);
    case 'activityRecipients/ACTIVITY_RECIPIENTS_SUCCESS':
      return updateActivity(state, action.activityId, {
        recipients: action.activityRecipients,
        loadingRecipients: false,
      });
    case 'activityRecipients/CURRENT_USER_ACTIVITY_RESPONSE_REQUEST':
      return updateActivity(state, action.activityId, {
        loadingCurrentUserRecipient: true,
      });
    case 'activityRecipients/ACTIVITY_RECIPIENTS_REQUEST':
      return updateActivity(state, action.activityId, {
        loadingRecipients: true,
      });
    case 'activityRecipients/PATCH_ACTIVITY_RECIPIENT_REQUEST_SUCCESS':
      return updateActivityRecipient(state, action.activityId, action.activityRecipient);
    case 'chats/LOAD_CHAT':
    case 'chats/APPEND_CHATS':
    case 'chats/LIST_CHATS':
      const chats = action.chats || [action.chat];
      const activityChats = chats.filter(chat => chat.isActivityType());

      if (activityChats.length === 0) {
        return state;
      }
      return activityChats.reduce((newState, chat) => {
        const chatActivity = chat.getActivity();
        return updateActivity(newState, chatActivity.id, {
          currentUserRecipient: chatActivity.get('viewing_user_response'),
          loadingCurrentUserRecipient: false,
        });
      }, state);
    default:
      return state;
  }
}

export type loadActivityRecipientsAction = {
  type: 'activityRecipients/ACTIVITY_RECIPIENTS_REQUEST',
  activityId: string,
};

export function loadActivityRecipients(activityId: string): loadActivityRecipientsAction {
  return {
    type: 'activityRecipients/ACTIVITY_RECIPIENTS_REQUEST',
    activityId,
  };
}

export type loadActivityRecipientsSuccessAction = {
  type: 'activityRecipients/ACTIVITY_RECIPIENTS_SUCCESS',
  activityRecipients: ActivityRecipients,
  activityId: string,
};

export function loadActivityRecipientsSuccess(activityId: string, activityRecipients: ActivityRecipients): loadActivityRecipientsSuccessAction {
  return {
    type: 'activityRecipients/ACTIVITY_RECIPIENTS_SUCCESS',
    activityRecipients,
    activityId,
  };
}

export type loadCurrentUserActivityResponseAction = {
  type: 'activityRecipients/CURRENT_USER_ACTIVITY_RESPONSE_REQUEST',
  activityId: string,
};

export function loadCurrentUserActivityResponse(activityId: string): loadCurrentUserActivityResponseAction {
  return {
    type: 'activityRecipients/CURRENT_USER_ACTIVITY_RESPONSE_REQUEST',
    activityId,
  };
}

export type updateLocalCurrentUserActivityResponseAction = {
  type: 'activityRecipients/UPDATE_LOCAL_CURRENT_USER_ACTIVITY',
  activityId: string,
  activityRecipient: ActivityRecipient,
};

export function updateLocalCurrentUserActivityResponse(activityId: string, activityRecipient: ActivityRecipient): updateLocalCurrentUserActivityResponseAction {
  return {
    type: 'activityRecipients/UPDATE_LOCAL_CURRENT_USER_ACTIVITY',
    activityRecipient,
    activityId,
  };
}

export type loadCurrentUserActivityResponseSuccessAction = {
  type: 'activityRecipients/CURRENT_USER_ACTIVITY_SUCCESS',
  activityId: string,
  activityRecipient: ActivityRecipient,
};

export function loadCurrentUserActivityResponseSuccess(activityRecipient: ActivityRecipient, activityId: string): loadCurrentUserActivityResponseSuccessAction {
  return {
    type: 'activityRecipients/CURRENT_USER_ACTIVITY_SUCCESS',
    activityRecipient,
    activityId,
  };
}

export type patchActivityRecipientAction = {
  type: 'activityRecipients/PATCH_ACTIVITY_RECIPIENT_REQUEST',
  activityId: string,
  recipientId: string,
  data: Object,
};

export function patchActivityRecipient(activityId: string, recipientId: string, data: Object): patchActivityRecipientAction {
  return {
    type: 'activityRecipients/PATCH_ACTIVITY_RECIPIENT_REQUEST',
    activityId,
    recipientId,
    data,
  };
}

export type patchActivityRecipientSuccessAction = {
  type: 'activityRecipients/PATCH_ACTIVITY_RECIPIENT_REQUEST_SUCCESS',
  activityId: string,
  activityRecipient: ActivityRecipient,
};

export function patchActivityRecipientSuccess(activityId: string, activityRecipient: ActivityRecipient): patchActivityRecipientSuccessAction {
  return {
    type: 'activityRecipients/PATCH_ACTIVITY_RECIPIENT_REQUEST_SUCCESS',
    activityRecipient,
    activityId,
  };
}

export type ActivityRecipientAction =
  loadCurrentUserActivityResponseAction |
  loadCurrentUserActivityResponseSuccessAction |
  loadActivityRecipientsAction |
  loadActivityRecipientsSuccessAction |
  patchActivityRecipientSuccessAction |
  patchActivityRecipientAction



// WEBPACK FOOTER //
// ./src/payments/reducers/activityRecipients.js